import { useCallback, useMemo } from 'react';
import { closeSnackbar as closeNotistackToast, MaterialDesignContent } from 'notistack';

import { styled } from '@mui/material';

import ToastCloseButton from './ToastCloseButton';
import ToastTextButton from './ToastTextButton';

import type { CustomContentProps, SnackbarKey } from 'notistack';
import type { PropsWithRefObject } from '../../types';
import type { CustomToastOptions } from './types';

export type BaseToastContentProps = CustomContentProps & CustomToastOptions;

export type ToastContentProps = PropsWithRefObject<HTMLDivElement> & BaseToastContentProps;

const StyledToastContent = styled(MaterialDesignContent)(({ theme }) => ({
  // container
  '&.notistack-MuiContent': {
    borderRadius: 2,
    boxShadow: 'none',
    padding: '16px 20px',
    flexWrap: 'initial',
    boxSizing: 'border-box',
    minWidth: 'auto',
    maxWidth: 1061,
    minHeight: 56,
  },

  // message
  '& #notistack-snackbar': {
    // icon + message width
    maxWidth: 828,
    minHeight: 20,
    padding: 0,
    fontFamily: theme.typography.fontFamily,
    fontSize: 16,
    fontWeight: 'normal',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: '24px',
    whiteSpace: 'pre-line',
  },

  // action
  '& > div:last-of-type': {
    marginRight: 0,
    marginLeft: 0,

    '& > * + *': {
      marginLeft: 8,
    },
  },

  '&.notistack-MuiContent-success': {
    backgroundColor: theme.color['surface/success/transparent/default'],
    '& button, & button:hover': {
      color: theme.color['text/inverse/default'],
    },
  },
  '&.notistack-MuiContent-info': {
    backgroundColor: theme.color['surface/transparent/strongest/default'],
    '& button, & button:hover': {
      color: theme.color['text/inverse/default'],
    },
  },
  '&.notistack-MuiContent-error': {
    backgroundColor: theme.color['surface/danger/transparent/default'],
    '& button, & button:hover': {
      color: theme.color['text/inverse/default'],
    },
  },
}));

const ToastContent = (props: ToastContentProps) => {
  const { ref, textButtonProps, withCloseButton = true, action, ...others } = props;
  const enableCustomAction = useMemo(
    () => !textButtonProps && !withCloseButton,
    [textButtonProps, withCloseButton],
  );

  const toastAction = useCallback(
    (key: SnackbarKey) => {
      return (
        <>
          {textButtonProps && <ToastTextButton {...textButtonProps} />}
          {withCloseButton && <ToastCloseButton onClick={() => closeNotistackToast(key)} />}
        </>
      );
    },
    [textButtonProps, withCloseButton],
  );

  return (
    <StyledToastContent
      ref={ref}
      action={enableCustomAction ? action : key => toastAction(key)}
      {...others}
    />
  );
};

export default ToastContent;
